import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { decideFrame, frameModeLabel, type FrameMode } from '@/lib/blog/frame';
import { englishNameFor, LTR_ISOLATE_ATTRS } from '@/lib/blog/locale-options';
import type { BlogClusterDetail, BlogLocaleEntry } from '@/lib/blog/types';

interface ViewerFrameProps {
  cluster: BlogClusterDetail;
  entry: BlogLocaleEntry;
  mode: FrameMode;
  onModeChange: (mode: FrameMode) => void;
  baseUrl: string;
}

function Blocked({
  tag,
  url,
  title,
  children,
}: {
  tag: string;
  url?: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-2 text-xs">
        {url !== undefined && (
          <span className="truncate font-mono text-muted-foreground" {...LTR_ISOLATE_ATTRS}>
            {url}
          </span>
        )}
        <span className="ml-auto shrink-0 rounded bg-background px-2 py-0.5">{tag}</span>
      </div>
      <div className="p-6">
        <h4 className="font-semibold">{title}</h4>
        <div className="mt-2 max-w-prose text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

/**
 * The frame region (M2).
 *
 * Every decision below is taken from the CMS payload before an `<iframe>` is
 * mounted, and the mounted frame is never asked anything (Q4). A cross-origin
 * frame cannot answer: `onload` fires identically for the article, for a 404
 * and for the marketing site's redirect to English — the last of which would
 * hide a missing locale by rendering a perfectly good English page in its
 * place.
 */
export function ViewerFrame({ cluster, entry, mode, onModeChange, baseUrl }: ViewerFrameProps) {
  const decision = decideFrame({ cluster, entry, mode, baseUrl });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-2xl border p-1" role="group" aria-label="Frame source">
          <Button
            size="sm"
            variant={mode === 'published' ? 'default' : 'ghost'}
            aria-pressed={mode === 'published'}
            onClick={() => { onModeChange('published'); }}
          >
            Published
          </Button>
          <Button
            size="sm"
            variant={mode === 'draft' ? 'default' : 'ghost'}
            aria-pressed={mode === 'draft'}
            onClick={() => { onModeChange('draft'); }}
          >
            Draft preview
          </Button>
        </div>
        {/* Always visible, never implicit: publishing what you think you are
            looking at is the mistake this sentence exists to prevent (Q3). */}
        <p className="text-sm text-muted-foreground" data-testid="frame-mode-label">
          {frameModeLabel(mode)}
        </p>
      </div>

      {decision.kind === 'render' && (
        <div className="rounded-md border" data-testid="viewer-frame">
          <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-2 text-xs">
            <span className="truncate font-mono text-muted-foreground" {...LTR_ISOLATE_ATTRS}>
              {decision.url}
            </span>
            <Badge variant="outline" className="ml-auto shrink-0">
              {decision.tag}
            </Badge>
          </div>
          {/*
            `sandbox` is defence in depth, NOT the mechanism. There is no token
            that stops a frame navigating itself — `allow-top-navigation`
            governs the parent — so what actually confines the reviewer to one
            post is the chrome-less marketing route this frame points at. The
            attribute is here to remove everything else a framed document could
            do, and it is deliberately as short as it can be:

            `allow-same-origin` — required, and it grants nothing against
            admin. Without it the document gets an OPAQUE origin, and
            marketing's CSP is written in terms of `'self'` (script-src,
            style-src, font-src, img-src). `'self'` never matches an opaque
            origin, so the page would load with no stylesheet, no fonts and no
            images — a preview of nothing. With it the frame keeps the
            MARKETING origin, which is cross-origin from admin either way, so
            the same-origin policy still separates the two. The documented
            hazard is pairing it with `allow-scripts`, which lets a frame strip
            its own sandbox — and only when it is same-origin with its
            embedder, which this never is.

            Everything else is omitted, and each omission is deliberate:

            `allow-scripts` — absent. The article is entirely server-rendered
            (`@atsready/blog-render` has no client component), so it reads and
            scrolls perfectly as static HTML. Withholding it means no script
            runs in the frame at all, which forecloses script-driven navigation
            outright rather than relying on there being no link to click.

            `allow-top-navigation*`, `allow-popups`, `allow-forms`,
            `allow-modals`, `allow-downloads` — absent. Each is a way out of
            the post or a way to interrupt the reviewer, and the preview needs
            none of them.
          */}
          <iframe
            title={`${cluster.slug} — ${entry.locale}`}
            src={decision.url}
            sandbox="allow-same-origin"
            className="h-[640px] w-full"
          />
          <p className="border-t px-3 py-2 text-xs text-muted-foreground">{decision.caption}</p>
        </div>
      )}

      {decision.kind === 'locale-missing' && (
        <Blocked tag="No copy" url={decision.url} title={`This post has no ${englishNameFor(decision.locale)} copy yet`}>
          Nothing is rendered because the URL does not exist. The marketing site would answer with a
          redirect to English, which would hide the gap from you rather than show it.
        </Blocked>
      )}

      {decision.kind === 'never-published' && (
        <Blocked tag="Not live" url={decision.url} title="This copy has never been published">
          There is no published build to frame. Switch to the draft preview to read what is waiting,
          or publish the cluster to give this address something to serve.
        </Blocked>
      )}

      {decision.kind === 'preview-missing' && (
        <Blocked tag="Not built" title="No preview exists for this draft">
          No preview deployment has ever been built for this post, so there is nothing to show. A
          build takes about ninety seconds.
        </Blocked>
      )}

      {decision.kind === 'preview-stale' && (
        <Blocked tag="Not built" title="No preview exists for this draft">
          The last preview was built on {decision.builtAt.slice(0, 10)} and the draft has moved on
          since. Showing the old build would misrepresent what you are about to publish, so it is
          deliberately not displayed.
        </Blocked>
      )}
    </div>
  );
}
